import { motion, useScroll, useTransform, useSpring, Variants, LazyMotion, domAnimation, AnimatePresence } from "framer-motion";
import { useState, useRef, ReactElement } from "react";
import { ContentLabels } from "../../types/navigation";

interface GalleryImage {
  url: string;
  alt: string;
  type: "horizontal" | "vertical" | "feature";
  category: "interior" | "exterior" | "amenities";
}

interface ProjectGalleryProps {
  images: GalleryImage[];
  language?: string;
  contentLabels?: ContentLabels;
}

interface ModalContentProps {
  selectedImage: number | null;
  images: GalleryImage[];
  direction: number;
  slideVariants: Variants;
  isDragging: boolean;
  handleDragEnd: (e: any, info: any) => void;
  setIsDragging: (value: boolean) => void;
  setSelectedImage: (value: number | null) => void;
  navigateImage: (direction: number) => void;
  labels: ContentLabels;
}

const ModalContent = ({ 
  selectedImage, 
  images, 
  direction, 
  slideVariants, 
  isDragging, 
  handleDragEnd, 
  setIsDragging, 
  setSelectedImage, 
  navigateImage,
  labels 
}: ModalContentProps): ReactElement | null => {
  if (selectedImage === null) return null;
  
  return (
    <motion.div
      key="modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center touch-none"
      onClick={() => setSelectedImage(null)}
    >
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={1}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        className="relative w-full h-full flex items-center justify-center"
      >
        <motion.img
          key={selectedImage}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 }
          }}
          src={images[selectedImage].url}
          alt={images[selectedImage].alt}
          className="max-h-[90vh] max-w-[90vw] object-contain pointer-events-none"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
        />
      </motion.div>

      {/* Navigation Buttons */}
      <button
        className="absolute top-4 right-4 text-white text-xl p-4"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedImage(null);
        }}
        aria-label={labels.closeGallery}
      >
        ✕
      </button>
      
      {selectedImage > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-4 rounded-full hover:bg-black/70 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            navigateImage(-1);
          }}
          aria-label="Previous image"
        >
          ←
        </button>
      )}
      
      {selectedImage < images.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-4 rounded-full hover:bg-black/70 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            navigateImage(1);
          }}
          aria-label="Next image"
        >
          →
        </button>
      )}

      {/* Image Counter */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
        {selectedImage + 1} / {images.length}
      </div>
    </motion.div>
  );
};

const ProjectGallery = ({ images, language, contentLabels }: ProjectGalleryProps): ReactElement => {
  // Default fallback labels if contentLabels is not provided
  const labels = contentLabels || {
    projectGallery: 'Project Gallery',
    closeGallery: 'Close gallery',
    // Other required fields with fallbacks
    availableUnitTypes: 'Available Unit Types',
    unitSize: 'Size:',
    startingFrom: 'Starting from',
    downloadBrochure: 'Download Brochure',
    contactExpert: 'Contact Expert',
    viewDetails: 'View Details',
    requestInfo: 'Request Information',
    bookTour: 'Book a Tour',
    whatsappAction: 'WhatsApp',
    callAction: 'Call Now',
    learnMore: 'Learn More',
    viewAll: 'View All',
    showMore: 'Show More',
    showLess: 'Show Less',
    paymentPlan: 'Payment Plan',
    downPayment: 'Down Payment',
    dueBy: 'Due by',
    noPaymentPlans: 'No payment plans available at this time.',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqDescription: 'Find answers to common questions about our property and investment opportunities',
    primeLocation: 'Prime Location',
    nearbyPlaces: 'Nearby Places',
    keyDistances: 'Key Distances',
    noNearbyPlaces: 'No nearby places available.',
    noKeyDistances: 'No key distances available.',
    dldPermitNumber: 'DLD Permit Number',
    scanQrCode: 'Scan QR Code for more information',
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    searchCountries: 'Search countries...',
    
    // Lead Form Content
    getPersonalizedDetails: 'Get Personalized Property Details',
    speakWithExperts: 'Speak with our property experts and get:',
    getStartedNow: 'Get Started Now',
    takesOnlySeconds: 'Takes only a few seconds',
    
    // Lead Form Benefits
    benefit1: 'Detailed floor plans & pricing',
    benefit2: 'Virtual tour of the property',
    benefit3: 'Payment plan options',
    benefit4: 'Investment analysis report',
    
    // Step Titles
    letsGetToKnowYou: "Let's get to know you",
    whatBringsYouHere: 'What brings you here today?',
    investmentPreferences: 'Investment Preferences',
    yourDreamHome: 'Your Dream Home',
    howToContactYou: 'How would you like us to contact you?',
    
    // Step Descriptions
    enterDetailsForPersonalized: 'Enter your details to receive personalized information',
    selectPrimaryInterest: 'Select your primary interest',
    tellUsInvestmentPreferences: 'Tell us about your investment preferences',
    tellUsIdealHome: 'Tell us about your ideal home',
    choosePreferredContact: 'Choose your preferred contact method',
    
    // Purpose Options
    investment: 'Investment',
    endUser: 'End User',
    lookingForInvestment: 'Looking for a profitable investment opportunity in Dubai\'s growing real estate market',
    searchingForHome: 'Searching for your perfect home in Dubai to live in',
    
    // Investment Labels
    investmentBudget: 'Investment Budget',
    investmentTimeline: 'Investment Timeline',
    
    // End User Labels
    preferredSize: 'Preferred Size',
    budgetRange: 'Budget Range',
    
    // Contact Options
    whatsappContact: 'WhatsApp',
    phoneCall: 'Phone Call',
    emailOption: 'Email',
    getInstantUpdates: 'Get instant updates via WhatsApp',
    speakDirectlyExperts: 'Speak directly with our experts',
    receiveDetailedEmail: 'Receive detailed information via email',
    
    // Success Messages
    willContactYou: 'We will contact you soon!',
    
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email',
    phoneRequired: 'Phone number is required'
  };

  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [direction, setDirection] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    damping: 15,
    stiffness: 30
  });

  const y = useTransform(
    smoothProgress,
    [0, 1],
    [50, -50]
  );

  const handleDragEnd = (e: any, info: any) => {
    setIsDragging(false);
    const threshold = 50;
    if (Math.abs(info.offset.x) > threshold) {
      const direction = info.offset.x > 0 ? -1 : 1;
      const newIndex = selectedImage! + direction;
      if (newIndex >= 0 && newIndex < images.length) {
        setDirection(direction);
        setSelectedImage(newIndex);
      }
    }
  };

  const navigateImage = (direction: number) => {
    const newIndex = selectedImage! + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setDirection(direction);
      setSelectedImage(newIndex);
    }
  };

  // Group images into layout sections
  const layoutSections = images.reduce((acc: GalleryImage[][], img, i) => {
    const sectionIndex = Math.floor(i / 3);
    if (!acc[sectionIndex]) acc[sectionIndex] = [];
    acc[sectionIndex].push(img);
    return acc;
  }, []);

  const slideVariants: Variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <>
      <LazyMotion features={domAnimation}>
        <section className="py-16 md:py-24 bg-gray-50" ref={containerRef}>
          <div className="container mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-center mb-12"
            >
              {labels.projectGallery}
            </motion.h2>

            <div className="space-y-8">
              {layoutSections.map((section, sectionIndex) => (
                <motion.div
                  key={sectionIndex}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  className="grid grid-cols-12 gap-4"
                >
                  {section.map((image, imageIndex) => {
                    const index = sectionIndex * 3 + imageIndex;
                    
                    return (
                      <motion.div
                        key={index}
                        className={`relative overflow-hidden rounded-2xl ${
                          image.type === "feature" 
                            ? "col-span-12" 
                            : image.type === "horizontal"
                            ? "col-span-12 md:col-span-8"
                            : "col-span-12 md:col-span-4"
                        }`}
                        variants={{
                          hidden: { 
                            opacity: 0,
                            scale: 0.8,
                            y: 100,
                            rotate: imageIndex % 2 === 0 ? -5 : 5
                          },
                          visible: {
                            opacity: 1,
                            scale: 1,
                            y: 0,
                            rotate: 0,
                            transition: {
                              type: "spring",
                              damping: 20,
                              stiffness: 100,
                              delay: index * 0.15,
                            }
                          }
                        }}
                        style={{ y }}
                        whileHover={{ 
                          scale: 1.02,
                          transition: { duration: 0.3 }
                        }}
                        onClick={() => !isDragging && setSelectedImage(index)}
                      >
                        <div 
                          className={`
                            aspect-[${image.type === "vertical" ? "4/5" : "16/9"}]
                            relative overflow-hidden group
                          `}
                        >
                          <motion.img
                            src={image.url}
                            alt={image.alt}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.4 }}
                          />
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            initial={false}
                          >
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                              <p className="text-white text-sm font-medium">
                                {image.alt}
                              </p>
                              <p className="text-white/80 text-xs capitalize">
                                {image.category}
                              </p>
                            </div>
                          </motion.div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </LazyMotion>

      {/* Fullscreen Image View - Outside LazyMotion */}
      <AnimatePresence initial={false} mode="wait">
        {selectedImage !== null && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
          >
            <ModalContent
              selectedImage={selectedImage}
              images={images}
              direction={direction}
              slideVariants={slideVariants}
              isDragging={isDragging}
              handleDragEnd={handleDragEnd}
              setIsDragging={setIsDragging}
              setSelectedImage={setSelectedImage}
              navigateImage={navigateImage}
              labels={labels}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectGallery;